import { useCloudstorageStore } from "@/stores/cloudstorage";
import { useCompanyStore } from "@/stores/company";
import bus from "/src/utils/event_bus";
import { useSelectedStore } from "@/stores/selected";
import { allCountersFromSaveOrClean, useQuantitiesStore } from "@/stores/quantities";
import { useReportsStore } from "@/stores/reports";
import { useAppStore } from "@/stores/app";
import { useConstructiblesStore } from "@/stores/constructibles";
import { useRecordsStore } from "@/stores/records";
import { useMultipanelRecordsStore } from "@/stores/multipanel_records";
import _ from "lodash"
import { watch } from "vue"

// WATCHERS THAT LAUNCH ACTIONS FROM "SELECTED" STATE CHANGES

let last_selected_project = null
let last_linked_stdlibs = []
let last_selected_mod = null
let last_selected_ass = null
let last_selected_modd = null
let last_selected_assd = null
let last_selected_mpgroup = null
let last_mpgroup_member_uuids_str = null

let last_date = null
let last_work_by_date = null


export const init_selected_watchers = function () {
    const company_store = useCompanyStore()
    const cloudstorage_store = useCloudstorageStore()
    const selected_store = useSelectedStore()
    const reports_store = useReportsStore()
    const records_store = useRecordsStore()
    const multipanel_records_store = useMultipanelRecordsStore()
    const app_store = useAppStore()
    const constructibles_store = useConstructiblesStore()
    const quantities_store = useQuantitiesStore()

    // Helper function to load resources by date
    const loadByDateIfNeeded = (date, uuid, reason = "") => {
        if (
            date !== "2000-01-01" &&        // Valid date
            uuid === "all" &&                // Company-wide mode
            app_store.byDateModeActive       // ByDate mode is active
        ) {
            console.log(`🍍👁️ -> Loading by date ${reason}`, date)
            app_store.pushLoading("project_resources")
            Promise.all([
                constructibles_store.loadModuleDesignsByDate(date),
                constructibles_store.loadAssemblyDesignsByDate(date),
                constructibles_store.loadBatchesByDate(date),
                constructibles_store.loadModulesByDate(date),
                constructibles_store.loadAssembliesByDate(date),
                constructibles_store.loadPartBatchesByDate(date),
            ]).finally(() => app_store.popLoading("project_resources"))
            return true  // Indicate that load was triggered
        }
        return false  // No load triggered
    }

    // The route's unit could not be loaded, and the store cleared it. The
    // watchers below skip a cleared assembly on purpose, so its proof, packages
    // and hot issues would stay on screen under the new address. Forget them
    // here, and forget which unit they belonged to, so going back to it loads
    // them again.
    bus.on("events/selected/actualRefused", () => {
        last_selected_ass = null
        last_selected_mod = null
        records_store.setRCP(null)
        cloudstorage_store.set_assembly_packages([])
        cloudstorage_store.set_module_packages([])
        selected_store.setHotIssues([])
    })

    // What is waiting on the project (docs/update-designs-proposal.md §5.7) is
    // asked again on every move between its pages: one light request, so the
    // left menu indicators never lag behind what another page just changed.
    const reloadAttention = _.debounce(() => selected_store.loadAttention(), 300)
    watch(() => app_store.route_name, () => reloadAttention())

    // A design update ended: the files, the designs, the units it moved and the
    // split counts are new. Reloaded here once, whatever page is open.
    bus.on("events/designUpdate/finished", () => {
        const uuid = selected_store.selected_project?.uuid
        if (!uuid || uuid == "all") return
        bus.emit("events/cloudstorage/reloadProjectPackages")
        constructibles_store.loadAssemblyDesigns(uuid)
        constructibles_store.loadModuleDesigns(uuid)
        if (!selected_store.stdlib_mode) {
            constructibles_store.loadAssemblies(uuid)
            constructibles_store.loadModules(uuid)
            quantities_store.loadSplit(uuid).catch(() => {})
        }
    })

    // The packages of the panel a subassembly is shown in: the IFC and the BOM
    // come from there. Watched rather than read on each store mutation: with a
    // unit open the panel is the one that unit belongs to, which depends on
    // the units loaded in the constructibles store too.
    watch(() => selected_store.subassdParentDesignUuid, (uuid) => {
        cloudstorage_store.clearParentAssemblyDesignPackages()
        if (uuid) cloudstorage_store.loadParentAssemblyDesignPackages(uuid)
    }, { immediate: true })

    selected_store.$subscribe((mutation, state) => {
        // -----------
        // PACKAGES
        // -----------


        console.log("🍍👁️ -> triggered selected watchers")

        let uuid = state.selected_project?.uuid
        if (uuid != last_selected_project) {

            // PACKAGES
            if (uuid != "all" && uuid != undefined)
                cloudstorage_store.loadProjectPackages(uuid)
            cloudstorage_store.set_project_packages([])  // always clean even if company wide
            cloudstorage_store.file_selection = []

            // PROJECT DB ASSETS
            // if (uuid == "all" || !uuid) {
            //     // Clean
            //     constructibles_store.setModuleDesigns([])
            //     constructibles_store.setAssemblyDesigns([])
            //     constructibles_store.setBatches([])
            //     constructibles_store.setModules([])
            //     constructibles_store.setAssemblies([])
            // }
            // else {
            //     app_store.pushLoading("project_resources")
            //     Promise.all([
            //         constructibles_store.loadModuleDesigns(uuid),
            //         constructibles_store.loadAssemblyDesigns(uuid),
            //         constructibles_store.loadBatches(uuid),
            //         constructibles_store.loadModules(uuid),
            //         constructibles_store.loadAssemblies(uuid),
            //     ]).finally(() => app_store.popLoading("project_resources"))
            // }


            // Clean (there was a change, it needs to be cleaned)
            constructibles_store.setModuleDesigns([])
            constructibles_store.setAssemblyDesigns([])
            constructibles_store.setBatches([])
            constructibles_store.setModules([])
            constructibles_store.setAssemblies([])
            constructibles_store.setPartBatches([])
            constructibles_store.setMultipanelGroups([])
            constructibles_store.list_hints = {}
            quantities_store.resetCountsForProject()

            // Actually load resources
            if (uuid != "all" && uuid) {
                app_store.pushLoading("project_resources")
                Promise.all([
                    constructibles_store.loadModuleDesigns(uuid),
                    constructibles_store.loadAssemblyDesigns(uuid),
                    constructibles_store.loadBatches(uuid),
                    constructibles_store.loadModules(uuid),
                    constructibles_store.loadAssemblies(uuid),
                    constructibles_store.loadPartBatches(uuid),
                    constructibles_store.loadMultipanelGroups(uuid),
                ]).finally(() => app_store.popLoading("project_resources"))
            }


            // QUANTITIES COUNTERS
            // The saved plan and the splitter's counts come from their own
            // endpoint; the matrix is rebuilt once they are in.
            quantities_store.loadPlan(uuid)
                .then(() => allCountersFromSaveOrClean())  // this is debounced store action
                .catch(() => {})

            // REPORTS
            reports_store.loadReportsWhenUsed(uuid)

            // WHAT IS WAITING (left menu indicators, Update designs)
            selected_store.clearAttention()
            selected_store.loadAttention()


            // TOUCH
            last_selected_project = uuid
        }


        let linked_stdlibs = state.selected_project?.linked_stdlibs || []
        if (!_.isEqual(linked_stdlibs, last_linked_stdlibs)) {

            constructibles_store.setStdlibAssemblyDesigns([])

            // Actually load resources
            if (linked_stdlibs.length > 0) {
                app_store.pushLoading("linked_stdlibs")
                Promise.all([
                    constructibles_store.loadStdlibAssemblyDesigns(linked_stdlibs),
                ]).finally(() => app_store.popLoading("linked_stdlibs"))
            }


            // QUANTITIES COUNTERS
            allCountersFromSaveOrClean()  // this is debounced store action


            last_linked_stdlibs = [...linked_stdlibs]
        }


        uuid = state.selected_mod?.uuid
        if (uuid != last_selected_mod) {
            last_selected_mod = uuid
            // Packages
            if (uuid != undefined)
                cloudstorage_store.loadModulePackages(uuid)
            cloudstorage_store.set_module_packages([])

            // RCP
            records_store.setRCP(null)
            if (uuid) records_store.loadRCP({ level: "module", uuid })

            // HOT ISSUES
            selected_store.setHotIssues([])
            if (!uuid) return
            selected_store.loadHotIssues({ module: uuid, same_design: true, include_solved: true })

        }

        uuid = state.selected_ass?.uuid
        if (uuid && uuid != last_selected_ass) {
            last_selected_ass = uuid
            // Packages
            if (uuid != undefined)
                cloudstorage_store.loadAssemblyPackages(uuid)
            cloudstorage_store.set_assembly_packages([])

            // RCP
            records_store.setRCP(null)
            if (uuid) records_store.loadRCP({ level: "assembly", uuid })

            // HOT ISSUES
            selected_store.setHotIssues([])
            if (!uuid) return
            selected_store.loadHotIssues({ assembly: uuid, same_design: true, include_solved: true })
        }

        // Multipanel group: eager-load every member's RCP + checklist package
        // so the toolbar button can show aggregate progress without waiting for
        // the dialog to open.
        const mpgroup_uuid = state.selected_multipanel_group?.group?.uuid || null
        const mpgroup_members = state.selected_multipanel_group?.members || []
        const mpgroup_member_uuids = mpgroup_members.map(m => m.assembly_uuid)
        const mpgroup_member_uuids_str = mpgroup_member_uuids.join(",")
        if (
            mpgroup_uuid != last_selected_mpgroup
            || mpgroup_member_uuids_str != last_mpgroup_member_uuids_str
        ) {
            last_selected_mpgroup = mpgroup_uuid
            last_mpgroup_member_uuids_str = mpgroup_member_uuids_str
            multipanel_records_store.clearAll()
            if (mpgroup_uuid && mpgroup_member_uuids.length > 0) {
                multipanel_records_store.loadAll(mpgroup_member_uuids)
                    .then(() => multipanel_records_store.loadPackages(mpgroup_member_uuids))
            }
        }

        uuid = state.selected_modd?.uuid
        if (uuid != last_selected_modd) {
            last_selected_modd = uuid
            if (uuid != undefined)
                cloudstorage_store.loadModuleDesignPackages(uuid)
            cloudstorage_store.set_module_design_packages([])
        }

        uuid = state.selected_assd?.uuid
        if (uuid != last_selected_assd) {
            last_selected_assd = uuid
            if (uuid != undefined)
                cloudstorage_store.loadAssemblyDesignPackages(uuid)
            cloudstorage_store.set_assembly_design_packages([])
        }

        // store.watch(
        //     (state) => state.v2.selected.selected_ass?.parent_assembly,
        //     (pass) => console.log("PARENT ASS!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!", pass)
        // )




        // BY DATE PROJECT SELECTION
        const date = state.selected_date
        uuid = state.selected_project?.uuid
        // Either change of date (being company wide), or change from pr to company_wide
        if (
            date != "2000-01-01" &&  // If placeholder date, DONT LOAD
            (
                (last_date != date && app_store.byDateModeActive) ||  // Option A: Date has changed
                (uuid == "all" && uuid != last_selected_project)       // Option B: Moving from pr to company-wide
            )
        ) {
            if (loadByDateIfNeeded(date, uuid, "(date/project changed)")) {
                last_date = date
                last_selected_project = uuid
            }
        }

    })

    // Watch for company data loading (handles first load race condition)
    company_store.$subscribe((mutation, state) => {
        const work_by_date = state.company?.work_by_date

        // React when work_by_date becomes true (handles first load race condition)
        if (work_by_date !== last_work_by_date && work_by_date === true) {
            const date = selected_store.selected_date
            const uuid = selected_store.selected_project?.uuid

            if (loadByDateIfNeeded(date, uuid, "(company data loaded)")) {
                last_date = date
            }
        }

        last_work_by_date = work_by_date
    })


    // PACKAGES EVENTS
    bus.on(
        "events/cloudstorage/reloadCompanyPackages",
        () => {
            let uuid = company_store.company?.uuid
            if (uuid) cloudstorage_store.loadCompanyPackages(uuid)
        }
    )
    bus.on(
        "events/cloudstorage/reloadProjectPackages",
        () => {
            let uuid = selected_store.selected_project?.uuid
            if (uuid) cloudstorage_store.loadProjectPackages(uuid)
        }
    )
    bus.on(
        "events/cloudstorage/reloadDesignPackages",
        () => {
            cloudstorage_store.set_assembly_design_packages([])
            cloudstorage_store.set_module_design_packages([])
            let assd_uuid = selected_store.selected_assd?.uuid
            let modd_uuid = selected_store.selected_modd?.uuid
            if (assd_uuid) cloudstorage_store.loadAssemblyDesignPackages(assd_uuid)
            if (modd_uuid) cloudstorage_store.loadModuleDesignPackages(modd_uuid)
        })
}
